import { useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MoveHorizontal } from "lucide-react";
import { FadeUp, MaskLines } from "../site/Reveal";

const FRAMES = [
  { src: "/img/v-front.webp", label: "Front", fit: "contain" },
  { src: "/img/v-angle.webp", label: "Angled", fit: "contain" },
  { src: "/img/v-side-r.webp", label: "Right", fit: "contain" },
  { src: "/img/v-back.webp", label: "Back", fit: "contain" },
  { src: "/img/v-side.webp", label: "Left", fit: "contain" },
  { src: "/img/v-top.webp", label: "Top", fit: "contain" },
  { src: "/img/v-bottom.webp", label: "Bottom", fit: "contain" },
  { src: "/img/v-inside.webp", label: "Inside", fit: "contain" },
];

export const AngleViewer = () => {
  const [i, setI] = useState(0);
  const drag = useRef(null);
  const down = (e) => { drag.current = { x: e.clientX, i }; e.currentTarget.setPointerCapture?.(e.pointerId); };
  const move = (e) => {
    if (!drag.current) return;
    const step = Math.round((e.clientX - drag.current.x) / 70);
    setI(((drag.current.i - step) % FRAMES.length + FRAMES.length) % FRAMES.length);
  };
  const f = FRAMES[i];

  return (
    <section className="bg-ivory py-24 md:py-36" data-testid="angle-viewer-section">
      <div className="mx-auto grid max-w-[1600px] gap-12 px-5 md:px-10 lg:grid-cols-12 lg:items-center">
        <div className="lg:col-span-4">
          <p className="label-mono">Every angle</p>
          <MaskLines inView lines={["See it from", <em key="a">all sides.</em>]} className="mt-4 font-display text-5xl font-light leading-[0.92] text-ink md:text-7xl" />
          <FadeUp className="mt-6 max-w-sm text-sm leading-relaxed text-graphite">Drag across the bag to turn it, or pick a view below. Angle views are shown in Beige.</FadeUp>
          <div className="mt-8 flex flex-wrap gap-2">
            {FRAMES.map((fr, k) => (
              <button key={fr.label} onClick={() => setI(k)} data-testid={`angle-btn-${k}`}
                className={`border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 ${k === i ? "border-ink bg-ink text-ivory" : "border-line text-ink hover:border-ink"}`}>
                {fr.label}
              </button>
            ))}
          </div>
        </div>
        <FadeUp className="lg:col-span-8">
          <div onPointerDown={down} onPointerMove={move} onPointerUp={() => { drag.current = null; }} onPointerCancel={() => { drag.current = null; }}
            data-testid="angle-viewer-stage" className="relative aspect-square cursor-grab bg-[#EFE9E1] touch-pan-y select-none overflow-hidden active:cursor-grabbing md:aspect-[16/11]">
            <AnimatePresence initial={false}>
              <motion.img key={f.src} src={f.src} alt={`MARKHOR belt bag ${f.label} view`} draggable={false}
                initial={{ opacity: 0, scale: 1.02 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.45 }}
                className={`absolute inset-0 h-full w-full ${f.fit === "contain" ? "object-contain" : "object-cover"}`} />
            </AnimatePresence>
            <div className="absolute bottom-4 left-4 flex items-center gap-2 bg-ivory/90 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-ink backdrop-blur">
              <MoveHorizontal className="h-3.5 w-3.5" /> Drag to rotate · {f.label}
            </div>
            <div className="absolute right-4 top-4 font-mono text-xs text-graphite">{String(i + 1).padStart(2, "0")} / {String(FRAMES.length).padStart(2, "0")}</div>
          </div>
        </FadeUp>
      </div>
    </section>
  );
};
